import { Check, Loader2, Pencil, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useGetClass } from '~entities/classes/api/get-class';
import { useGetEnrollmentTotalPayments } from '~entities/classes/api/get-enrollment-total-payments';
import { useGetCalculateDiscountByEntity } from '~entities/discount/api/get-calculate-discount-by-entity';
import {
  isAlreadyEnrolledNoop,
  previewEnrollment,
  type PreviewChargeBreakdown,
  usePreviewEnrollment,
} from '~entities/enrollment/api/preview-enrollment';
import {
  getStartEnrollmentOutcome,
  useStartEnrollment,
} from '~entities/enrollment/api/start-enrollment';
import { useGetFamilyByStudent } from '~entities/family';
import { useGetParentById } from '~entities/parent/api/get-parent-by-id';
import { useCheckPaymentMethod } from '~entities/payment/api/check-payment-method';
import {
  type OnlinePaymentMethod,
  PAYMENT_METHOD_LABELS,
  usePaymentMethodOptions,
} from '~entities/payment/api/get-allowed-payment-methods';
import {
  formatEnrollmentSavedMethodLabel,
  pickEnrollmentCatalogDefaultMethod,
  toOnlinePaymentMethod,
  useGetEnrollmentPaymentMethods,
} from '~entities/payment/api/get-enrollment-payment-methods';
import {
  getParentSavedPaymentMethods,
  pickPaymentMethodId,
  useGetParentSavedPaymentMethods,
} from '~entities/payment/api/get-parent-saved-payment-methods';
import { useGetSessionFeeStatus } from '~entities/session/api/get-session-fee-status';
import { useAddStudentToEntity } from '~entities/stripe/api/add-student-to-class';
import { useCreateTrialEnrollment } from '~entities/student/api/create-trial-enrollment';
import {
  type TrialSlot,
  useGetAvailableTrialSlots,
} from '~entities/student/api/get-available-trial-slots';
import { useGetStudentById } from '~entities/student/api/get-student-by-id';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';
import { PaymentMethod } from '~entities/wallet/model/types';

import { PayingParentSelect } from '~features/enrollment/ui/paying-parent-select';
import { ClassSelector } from '~features/payment/ui/class-selector';
import { SessionSelector } from '~features/shared/session-selector';
import { EnrollStudentConfirmation } from '~features/students/ui/enroll-student-confirmation';
import {
  isEnrollConfirmationHeld,
  preventDismissForSelectMenuOrConfirm,
  shouldIgnoreEnrollDialogClose,
  shouldPreventEnrollDialogDismiss,
} from '~features/students/utils/enroll-dialog-dismiss';
import {
  calculateDueNowDiscount,
  calculateManualEnrollmentDiscount,
  enrollmentConcessionScopeNote,
} from '~features/students/utils/enrollment-summary-discount';
import {
  isEnrollmentPaymentsForClass,
  isHourlyFirstMonthEnrollment,
  resolveHourlyFirstMonthPreviewTuition,
  resolveHourlyLastMonthPreviewAmount,
} from '~features/students/utils/hourly-last-month-preview';

import { useGetCurrency } from '~shared/api/currency-api';
import { formatBillingCycle } from '~shared/constants/tuition-billing-cycle';
import { formatTrialTimeRange } from '~shared/helpers/format-trial-datetime';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Calendar } from '~shared/shadcn/ui/calendar';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';
import { calculateProratedTuition } from '~shared/utils/proration';

interface PaymentMethodOptionProps {
  id: string;
  value: OnlinePaymentMethod;
  label: string;
  checkData?: { hasPaymentMethod?: boolean };
}

const PaymentMethodOption = ({
  id,
  value,
  label,
  checkData,
}: PaymentMethodOptionProps) => (
  <div className='flex items-center space-x-2'>
    <RadioGroupItem value={value} id={id} />
    <Tooltip>
      <TooltipTrigger asChild>
        <Label
          htmlFor={id}
          className={cn(
            checkData?.hasPaymentMethod ? 'text-green-400' : 'text-red-400',
          )}
        >
          {label}
        </Label>
      </TooltipTrigger>
      <TooltipContent>
        <p>
          {checkData?.hasPaymentMethod
            ? `${label} is available`
            : `${label} is not available`}
        </p>
      </TooltipContent>
    </Tooltip>
  </div>
);

const formatLocalDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocalDateKey = (dateValue?: string | Date | null) => {
  if (!dateValue) return null;

  if (typeof dateValue === 'string') {
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateValue);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const localDate = new Date(Number(year), Number(month) - 1, Number(day));
      const isValidDate =
        localDate.getFullYear() === Number(year) &&
        localDate.getMonth() === Number(month) - 1 &&
        localDate.getDate() === Number(day);

      return isValidDate ? `${year}-${month}-${day}` : null;
    }
  }

  const parsedDate =
    dateValue instanceof Date ? dateValue : new Date(dateValue);
  return Number.isNaN(parsedDate.getTime())
    ? null
    : formatLocalDateKey(parsedDate);
};

const getRuntimeSession = <TSession,>(entity?: { session?: TSession } | null) =>
  entity?.session;

const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const normalizeClassDay = (day?: string | null) => {
  const trimmed = day?.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toLowerCase();
  const dayMap: Record<string, string> = {
    sun: 'Sunday',
    sunday: 'Sunday',
    mon: 'Monday',
    monday: 'Monday',
    tue: 'Tuesday',
    tues: 'Tuesday',
    tuesday: 'Tuesday',
    wed: 'Wednesday',
    weds: 'Wednesday',
    wednesday: 'Wednesday',
    thu: 'Thursday',
    thur: 'Thursday',
    thurs: 'Thursday',
    thursday: 'Thursday',
    fri: 'Friday',
    friday: 'Friday',
    sat: 'Saturday',
    saturday: 'Saturday',
  };

  return dayMap[normalized] ?? null;
};

type TrialScheduleSource =
  | {
      availability?: Array<{ day?: string }>;
      days?: Array<string>;
    }
  | null
  | undefined;

const getAllowedClassDays = (
  classDetails: TrialScheduleSource,
): Array<string> => {
  const availabilityDays = Array.isArray(classDetails?.availability)
    ? classDetails.availability
        .map((slot: { day?: string }) => normalizeClassDay(slot.day))
        .filter((value: string | null): value is string => !!value)
    : [];

  const configuredDays =
    availabilityDays.length > 0
      ? availabilityDays
      : Array.isArray(classDetails?.days)
        ? classDetails.days
            .map((day: string) => normalizeClassDay(day))
            .filter((value: string | null): value is string => !!value)
        : [];

  return Array.from(new Set(configuredDays));
};

interface EnrollStudentModalProps {
  studentId: string;
  preSelectedSession?: { label: string; value: string } | null;
  preSelectedClass?: { label: string; value: string } | null;
  initialEnrollmentType?: 'regular' | 'trial';
  initialTrialDate?: string;
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  parentId?: string;
  /** Called after successful trial enrollment (e.g. to navigate to Classes tab) */
  onTrialEnrollmentSuccess?: () => void;
}

export const EnrollStudentModal = ({
  studentId,
  preSelectedSession,
  preSelectedClass,
  initialEnrollmentType = 'regular',
  initialTrialDate = '',
  dialogOpen,
  setDialogOpen,
  parentId,
  onTrialEnrollmentSuccess,
}: EnrollStudentModalProps) => {
  // const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<{
    label: string;
    value: string;
  } | null>(preSelectedSession ?? null);
  const [selectedClass, setSelectedClass] = useState<{
    label: string;
    value: string;
  } | null>(preSelectedClass ?? null);
  const [discountType, setDiscountType] = useState<'amount' | 'percentage'>(
    'amount',
  );
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [collectUpfront, setCollectUpfront] = useState<boolean>(false);
  const [skipPaymentMethod, setSkipPaymentMethod] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  // Class picker is modal={false} so Session/Class menus stay clickable.
  // The confirm dialog is modal and steals focus, which dismisses this picker.
  // The close reset then clears confirmation, so Enroll flashes and vanishes.
  // Hold dismiss from the moment we open confirmation until the focus shift settles.
  const holdEnrollDialogRef = useRef(false);
  const [discountCategory, setDiscountCategory] = useState<
    null | 'discount' | 'scholarship'
  >(null);
  const [applyDiscountTo, setApplyDiscountTo] = useState<'first' | 'all'>(
    'all',
  );
  const [paymentMethod, setPaymentMethod] = useState<
    OnlinePaymentMethod | undefined
  >(undefined);
  const [isEditingTuition, setIsEditingTuition] = useState(false);
  const [tuitionValue, setTuitionValue] = useState<number | undefined>(
    undefined,
  );
  const [tuitionError, setTuitionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enrollmentType, setEnrollmentType] = useState<'regular' | 'trial'>(
    initialEnrollmentType,
  );
  const [trialDate, setTrialDate] = useState(initialTrialDate);
  const [selectedSlot, setSelectedSlot] = useState<TrialSlot | null>(null);
  const [lastWarnedKey, setLastWarnedKey] = useState<string | null>(null);
  const [resolvedPaymentMethodId, setResolvedPaymentMethodId] = useState<
    string | undefined
  >(undefined);
  const [payingParentId, setPayingParentId] = useState<string | undefined>(
    undefined,
  );
  const [enrollmentPreview, setEnrollmentPreview] = useState<{
    amountDueTotal: number;
    charges: Array<PreviewChargeBreakdown>;
  } | null>(null);
  const [stripeConfig, setStripeConfig] = useState<{
    clientSecret: string;
    publishableKey: string;
    stripeAccount?: string;
  } | null>(null);

  const { data: studentData } = useGetStudentById(studentId);
  const { data: familyByStudent } = useGetFamilyByStudent(studentId);
  const effectiveParentId = parentId ?? studentData?.parentId;
  const billingParentForEnrollment =
    payingParentId ||
    familyByStudent?.family?.billingParentId ||
    effectiveParentId;

  const { data: classDetails } = useGetClass({
    classId: selectedClass?.value ?? '',
  });

  // Get total payments for the selected class
  const { data: studioDetails } = useGetStudioDetails();
  const studioTimezone = studioDetails?.timezone;

  const { data: totalPaymentsData, isFetching: isTotalPaymentsFetching } =
    useGetEnrollmentTotalPayments(
      {
        enrollmentId: selectedClass?.value ?? '',
        studentId,
        studioId: studioDetails?._id,
      },
      {
        enabled: !!selectedClass?.value && !!studentId && !!studioDetails?._id,
      },
    );

  const { data: currencyData } = useGetCurrency();
  const { hasAdminAccess, hasStaffAccess } = useCurrentUserRole();
  const canOverrideClassCapacity = hasAdminAccess || hasStaffAccess;
  const locationId = studioDetails?.locationId ?? '';
  const isV2Enrollment = studioDetails?.isV2 === true;

  const { isCardAllowed, isAchAllowed, isBecsAllowed, firstAllowedMethod } =
    usePaymentMethodOptions(isV2Enrollment ? '' : locationId);

  const { data: parentData } = useGetParentById(
    (isV2Enrollment ? billingParentForEnrollment : effectiveParentId) ?? '',
  );
  const { data: savedPaymentMethods } = useGetParentSavedPaymentMethods({
    email: isV2Enrollment ? undefined : parentData?.email,
    locationId: isV2Enrollment ? undefined : locationId,
    parentId: isV2Enrollment ? billingParentForEnrollment : effectiveParentId,
  });

  const { data: enrollmentPaymentMethods, isLoading: isLoadingEnrollmentPms } =
    useGetEnrollmentPaymentMethods({
      parentId: billingParentForEnrollment,
      enabled: isV2Enrollment && !!billingParentForEnrollment,
    });

  const { data: checkCardPaymentMethodData } = useCheckPaymentMethod({
    parentId: isV2Enrollment ? '' : (effectiveParentId ?? ''),
    paymentMethod: PaymentMethod.CARD,
  });

  const { data: checkBankPaymentMethodData } = useCheckPaymentMethod({
    parentId: isV2Enrollment ? '' : (effectiveParentId ?? ''),
    paymentMethod: PaymentMethod.US_BANK_ACCOUNT,
  });

  const { data: checkBecsPaymentMethodData } = useCheckPaymentMethod({
    parentId: isV2Enrollment ? '' : (effectiveParentId ?? ''),
    paymentMethod: PaymentMethod.AU_BECS_DEBIT,
  });

  const { data: sessionFeeStatusData } = useGetSessionFeeStatus(
    {
      sessionId: selectedSession?.value ?? '',
      parentId:
        (isV2Enrollment ? billingParentForEnrollment : effectiveParentId) ??
        '',
      studentId,
    },
    {
      enabled:
        !!selectedSession?.value &&
        !!(isV2Enrollment ? billingParentForEnrollment : effectiveParentId),
    },
  );

  const { data: calculateDiscountByEntityData } =
    useGetCalculateDiscountByEntity({
      studentId,
      entityType: 'enrollment',
      entityId: selectedClass?.value ?? '',
    });

  const startEnrollmentMutation = useStartEnrollment();
  const previewEnrollmentMutation = usePreviewEnrollment();
  const addStudentToEntityMutation = useAddStudentToEntity();
  const createTrialEnrollmentMutation = useCreateTrialEnrollment();

  const trialSlotsParams =
    enrollmentType === 'trial' && selectedClass?.value && trialDate
      ? {
          classId: selectedClass.value,
          startDate: trialDate,
          endDate: trialDate,
        }
      : null;
  const {
    data: trialSlotsData,
    isFetching: isFetchingSlots,
    isError: isSlotsError,
  } = useGetAvailableTrialSlots(trialSlotsParams);

  const confirmationHeld = () =>
    isEnrollConfirmationHeld(showConfirmation, holdEnrollDialogRef.current);

  const openEnrollmentConfirmation = () => {
    holdEnrollDialogRef.current = true;
    setShowConfirmation(true);
  };

  const releaseEnrollDialogHold = () => {
    window.setTimeout(() => {
      holdEnrollDialogRef.current = false;
    }, 0);
  };

  const closeEnrollmentConfirmation = () => {
    setShowConfirmation(false);
    releaseEnrollDialogHold();
  };

  const handleEnrollDialogOpenChange = (open: boolean) => {
    if (shouldIgnoreEnrollDialogClose(open, confirmationHeld())) return;
    setDialogOpen(open);
  };

  const blockEnrollDialogFocusDismiss = (event: {
    preventDefault: () => void;
  }) => {
    if (shouldPreventEnrollDialogDismiss('focus', confirmationHeld())) {
      event.preventDefault();
    }
  };

  const blockEnrollDialogDismiss = (event: {
    preventDefault: () => void;
    target?: EventTarget | null;
  }) => {
    // Age-warning / confirm hold, or a click on a body-portaled select menu.
    if (shouldPreventEnrollDialogDismiss('pointer', confirmationHeld())) {
      event.preventDefault();
      return;
    }
    preventDismissForSelectMenuOrConfirm(event, false);
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!dialogOpen) {
      holdEnrollDialogRef.current = false;
      setSelectedSession(preSelectedSession ?? null);
      setSelectedClass(preSelectedClass ?? null);
      setDiscountCategory(null);
      setDiscountType('amount');
      setDiscountAmount(0);
      setDiscountPercentage(0);
      setApplyDiscountTo('all');
      setCollectUpfront(false);
      setShowConfirmation(false);
      setSkipPaymentMethod(false);
      setIsEditingTuition(false);
      setTuitionValue(undefined);
      setTuitionError(null);
      setIsSubmitting(false);
      setEnrollmentType(initialEnrollmentType);
      setTrialDate(initialTrialDate);
      setSelectedSlot(null);
      setResolvedPaymentMethodId(undefined);
      setPayingParentId(undefined);
      setEnrollmentPreview(null);
      setStripeConfig(null);
    }
  }, [
    dialogOpen,
    preSelectedSession,
    preSelectedClass,
    initialEnrollmentType,
    initialTrialDate,
  ]);

  // Sync paymentMethod with allowed methods when they load or change.
  // Skip when skipPaymentMethod is true so checking "no payment method needed"
  // does not immediately re-select a method and send it to the backend.
  // V2 selects an explicit saved paymentMethodId from the enrollment catalog.
  useEffect(() => {
    if (skipPaymentMethod) return;

    if (isV2Enrollment) {
      const catalog = enrollmentPaymentMethods;
      if (!catalog) return;

      const selectedStillValid =
        !!resolvedPaymentMethodId &&
        catalog.savedMethods.some(
          (method) => method.id === resolvedPaymentMethodId,
        );
      if (selectedStillValid) return;

      const defaultMethod = pickEnrollmentCatalogDefaultMethod(catalog);
      if (!defaultMethod) {
        setResolvedPaymentMethodId(undefined);
        setPaymentMethod(undefined);
        return;
      }
      setResolvedPaymentMethodId(defaultMethod.id);
      setPaymentMethod(toOnlinePaymentMethod(defaultMethod.type));
      return;
    }

    if (!firstAllowedMethod) return;
    const isCurrentAllowed =
      (paymentMethod === PaymentMethod.CARD && isCardAllowed) ||
      (paymentMethod === PaymentMethod.US_BANK_ACCOUNT && isAchAllowed) ||
      (paymentMethod === PaymentMethod.AU_BECS_DEBIT && isBecsAllowed);
    if (!paymentMethod || !isCurrentAllowed) {
      setPaymentMethod(firstAllowedMethod);
    }
  }, [
    isV2Enrollment,
    enrollmentPaymentMethods,
    resolvedPaymentMethodId,
    paymentMethod,
    skipPaymentMethod,
    isCardAllowed,
    isAchAllowed,
    isBecsAllowed,
    firstAllowedMethod,
  ]);

  // Reset class when session changes (but not if we have a pre-selected class)
  useEffect(() => {
    if (!preSelectedClass) {
      setSelectedClass(null);
    }
  }, [selectedSession, preSelectedClass]);

  // Ensure regular-only editing state never blocks trial enrollment.
  useEffect(() => {
    if (enrollmentType === 'trial') {
      setIsEditingTuition(false);
      setTuitionError(null);
    }
  }, [enrollmentType]);

  // Reset tuition value when class changes or when collectUpfront changes
  useEffect(() => {
    if (classDetails) {
      // When collectUpfront changes, update the tuition value accordingly
      if (
        collectUpfront &&
        totalPaymentsData?.totalPayments &&
        classDetails.tuitionBillingCycle !== 'One Time'
      ) {
        // Explicitly set the tuition value to the total for all payments
        const totalValue =
          classDetails.tuitionFee * totalPaymentsData.totalPayments;
        setTuitionValue(totalValue);
      } else {
        // Reset to the base fee when not collecting upfront
        setTuitionValue(classDetails.tuitionFee);
      }
      setTuitionError(null);
    }
  }, [classDetails, collectUpfront, totalPaymentsData?.totalPayments]);

  // Memoize the tuition value to prevent recalculations
  const currentTuition = useMemo(() => {
    // If there's an error, use class default or 1
    if (tuitionError) {
      return classDetails?.tuitionFee ?? 1;
    }

    // Get the base tuition value without any multiplication
    const baseTuition = tuitionValue ?? classDetails?.tuitionFee ?? 1;

    // Only apply the multiplication for upfront payments if we haven't already done so
    // This check prevents double-multiplication when tuitionValue has already been set
    // to the total (which happens when we first check "collect upfront")
    const shouldMultiply =
      collectUpfront &&
      totalPaymentsData?.totalPayments &&
      classDetails?.tuitionBillingCycle !== 'One Time' &&
      // Check if we haven't already set the tuition value to the multiplied value
      // when we enabled collectUpfront
      tuitionValue === classDetails?.tuitionFee;

    if (shouldMultiply) {
      return baseTuition * totalPaymentsData.totalPayments;
    }

    return baseTuition;
  }, [
    tuitionValue,
    classDetails?.tuitionFee,
    tuitionError,
    collectUpfront,
    totalPaymentsData?.totalPayments,
    classDetails?.tuitionBillingCycle,
  ]);

  // Baseline tuition used when enabling upfront mode (before any manual override).
  const defaultUpfrontTuition = useMemo(() => {
    if (
      !classDetails ||
      !collectUpfront ||
      !totalPaymentsData?.totalPayments ||
      classDetails.tuitionBillingCycle === 'One Time'
    ) {
      return undefined;
    }
    return classDetails.tuitionFee * totalPaymentsData.totalPayments;
  }, [classDetails, collectUpfront, totalPaymentsData?.totalPayments]);

  // True when user changed tuition via pencil edit in upfront flow.
  const hasManualUpfrontTuitionOverride = useMemo(() => {
    if (defaultUpfrontTuition === undefined || tuitionValue === undefined) {
      return false;
    }
    return (
      Math.round(tuitionValue * 100) !== Math.round(defaultUpfrontTuition * 100)
    );
  }, [defaultUpfrontTuition, tuitionValue]);

  const selectedEnrollmentId = selectedClass?.value;

  const matchedEnrollmentPayments = useMemo(() => {
    if (
      !isEnrollmentPaymentsForClass(totalPaymentsData, selectedEnrollmentId)
    ) {
      return undefined;
    }
    return totalPaymentsData;
  }, [selectedEnrollmentId, totalPaymentsData]);

  const hourlyLastMonthPreviewAmount = useMemo(
    () =>
      resolveHourlyLastMonthPreviewAmount(
        totalPaymentsData,
        selectedEnrollmentId,
      ),
    [selectedEnrollmentId, totalPaymentsData],
  );

  // Check if class is full
  const isClassFull = useMemo(() => {
    if (!classDetails?.maxSize || classDetails.maxSize <= 0) {
      return false; // No capacity limit
    }
    return classDetails.remainingSeats <= 0;
  }, [classDetails]);

  const allowedTrialDays = useMemo(
    () => getAllowedClassDays(classDetails),
    [classDetails],
  );

  const allowedTrialDayIndexes = useMemo(
    () =>
      new Set(
        allowedTrialDays
          .map((day) =>
            WEEKDAY_LABELS.indexOf(day as (typeof WEEKDAY_LABELS)[number]),
          )
          .filter((index) => index >= 0),
      ),
    [allowedTrialDays],
  );

  const trialButtonDisabled = useMemo(() => {
    return (
      !selectedClass || !selectedSlot || createTrialEnrollmentMutation.isPending
    );
  }, [selectedClass, selectedSlot, createTrialEnrollmentMutation.isPending]);

  const trialScheduleWarnings = useMemo(
    () => trialSlotsData?.scheduleWarnings ?? [],
    [trialSlotsData?.scheduleWarnings],
  );

  useEffect(() => {
    if (enrollmentType !== 'trial' || isFetchingSlots) {
      return;
    }

    const firstWarning = trialScheduleWarnings[0];
    if (firstWarning?.message) {
      const warningKey = `${selectedClass?.value}-${trialDate}-${firstWarning.message}`;
      if (lastWarnedKey !== warningKey) {
        toast.warning(firstWarning.message);
        setLastWarnedKey(warningKey);
      }
    } else {
      setLastWarnedKey(null);
    }
  }, [
    enrollmentType,
    isFetchingSlots,
    trialScheduleWarnings,
    trialDate,
    selectedClass?.value,
    lastWarnedKey,
  ]);

  // Validate tuition value and return true if valid
  const isTuitionValid = () => {
    return !tuitionError && tuitionValue !== undefined && tuitionValue > 0;
  };

  const classDetailsSession = getRuntimeSession(classDetails);

  const isHourlyLastMonthCheckout = useMemo(() => {
    if (collectUpfront) return false;
    return (
      classDetails?.tuitionBillingCycle === 'hourly' &&
      !!classDetailsSession?.chargeLastMonthAtCheckout
    );
  }, [
    classDetails?.tuitionBillingCycle,
    classDetailsSession?.chargeLastMonthAtCheckout,
    collectUpfront,
  ]);

  const isHourlyFirstMonthCheckout = useMemo(() => {
    if (collectUpfront) return false;
    return isHourlyFirstMonthEnrollment(classDetails);
  }, [classDetails, collectUpfront]);

  const [hourlyFirstMonthPreviewAmount, setHourlyFirstMonthPreviewAmount] =
    useState<number | undefined>(undefined);
  const [isHourlyFirstMonthPreviewLoading, setIsHourlyFirstMonthPreviewLoading] =
    useState(false);

  // V2 hourly first-month: class.tuitionFee is 0, so load the real delta into
  // the enroll summary (confirm already used previewEnrollment).
  useEffect(() => {
    if (
      !isV2Enrollment ||
      !isHourlyFirstMonthCheckout ||
      !selectedClass?.value ||
      !selectedSession?.value ||
      !classDetails ||
      !studentId
    ) {
      setHourlyFirstMonthPreviewAmount(undefined);
      setIsHourlyFirstMonthPreviewLoading(false);
      return;
    }

    const studioId = classDetails.studio || studioDetails?._id;
    if (!studioId) {
      setHourlyFirstMonthPreviewAmount(undefined);
      return;
    }

    let cancelled = false;
    setIsHourlyFirstMonthPreviewLoading(true);

    void previewEnrollment({
      studioId,
      source: 'studio_portal',
      mode: 'manual',
      requests: [
        {
          entityId: selectedClass.value,
          entityType: 'class',
          sessionId: selectedSession.value,
          collectUpfront: false,
          skipPaymentMethod: true,
          students: [
            {
              studentId,
              ...(payingParentId ? { payingParentId } : {}),
            },
          ],
        },
      ],
    })
      .then((preview) => {
        if (cancelled) return;
        setHourlyFirstMonthPreviewAmount(
          resolveHourlyFirstMonthPreviewTuition(preview.charges, studentId),
        );
      })
      .catch(() => {
        if (!cancelled) setHourlyFirstMonthPreviewAmount(undefined);
      })
      .finally(() => {
        if (!cancelled) setIsHourlyFirstMonthPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isV2Enrollment,
    isHourlyFirstMonthCheckout,
    selectedClass?.value,
    selectedSession?.value,
    classDetails,
    studentId,
    studioDetails?._id,
    payingParentId,
  ]);

  // Tuition amount to use: prorated when applicable, otherwise full current tuition
  const effectiveTuition = useMemo(() => {
    if (
      !classDetails ||
      collectUpfront ||
      classDetails.tuitionBillingCycle === 'One Time'
    ) {
      return currentTuition;
    }

    // Hourly last-month prepay: always use backend incremental delta (tuitionFee is 0).
    if (isHourlyLastMonthCheckout && !hasManualUpfrontTuitionOverride) {
      if (hourlyLastMonthPreviewAmount != null) {
        return hourlyLastMonthPreviewAmount;
      }
      if (isTotalPaymentsFetching) {
        return currentTuition;
      }
      return 0;
    }

    // Hourly first-month family/student: backend schedule delta (tuitionFee is 0).
    if (
      isV2Enrollment &&
      isHourlyFirstMonthCheckout &&
      !hasManualUpfrontTuitionOverride
    ) {
      if (hourlyFirstMonthPreviewAmount != null) {
        return hourlyFirstMonthPreviewAmount;
      }
      if (isHourlyFirstMonthPreviewLoading) {
        return currentTuition;
      }
      return 0;
    }

    // Studio proration disabled → no proration
    if (studioDetails?.prorationEnabled === false) {
      return currentTuition;
    }

    // Class-level proration disabled → no proration
    if (!classDetails.prorateTuition) {
      return currentTuition;
    }

    // When chargeLastMonthAtCheckout is enabled and the class is scheduled
    // (enrolling before billing date), the checkout charges the last invoice
    const payments = matchedEnrollmentPayments?.payments;
    const session = getRuntimeSession(classDetails);
    const todayStr = formatLocalDateKey(new Date());
    const billingDateStr = getLocalDateKey(session?.billingDate);
    const isScheduled = billingDateStr && todayStr < billingDateStr;
    const chargeLastMonth = isScheduled && !!session?.chargeLastMonthAtCheckout;

    if (payments?.length && !hasManualUpfrontTuitionOverride) {
      const checkoutPayment = chargeLastMonth
        ? payments[payments.length - 1]
        : payments[0];
      return checkoutPayment?.amount ?? 0;
    }

    if (matchedEnrollmentPayments?.totalAmount != null) {
      return matchedEnrollmentPayments.totalAmount;
    }

    // When chargeLastMonth is active but backend data hasn't loaded yet,
    // fall back to full tuition (last period proration can't be computed client-side)
    if (chargeLastMonth) {
      return currentTuition;
    }

    // Fallback to frontend estimation when backend data isn't loaded yet
    const proratedAmount = calculateProratedTuition({
      tuitionFee: currentTuition,
      tuitionBillingCycle: classDetails.tuitionBillingCycle,
      startDate: classDetails.startDate,
      endDate: classDetails.endDate,
      billingDay: classDetails.billingDay,
      billingFrequencyDays: classDetails.billingFrequencyDays,
      prorationBasis: studioDetails?.prorationBasis,
      classDays: classDetails.days,
      timeZone: studioDetails?.timezone,
    });

    return proratedAmount;
  }, [
    classDetails,
    studioDetails?.prorationEnabled,
    studioDetails?.prorationBasis,
    studioDetails?.timezone,
    currentTuition,
    collectUpfront,
    matchedEnrollmentPayments,
    hasManualUpfrontTuitionOverride,
    isHourlyLastMonthCheckout,
    hourlyLastMonthPreviewAmount,
    isTotalPaymentsFetching,
    isV2Enrollment,
    isHourlyFirstMonthCheckout,
    hourlyFirstMonthPreviewAmount,
    isHourlyFirstMonthPreviewLoading,
  ]);

  const getUpfrontTuition = useMemo(() => {
    if (!collectUpfront || classDetails?.tuitionBillingCycle === 'One Time') {
      return effectiveTuition;
    }

    // When user manually edits upfront tuition via pencil input, treat that
    // edited amount as the source of truth for the upfront tuition preview.
    if (hasManualUpfrontTuitionOverride && tuitionValue !== undefined) {
      return tuitionValue;
    }

    // Use backend total amount when available (includes prorated first + last periods)
    if (
      matchedEnrollmentPayments?.totalAmount != null &&
      !hasManualUpfrontTuitionOverride
    ) {
      return matchedEnrollmentPayments.totalAmount;
    }

    // Fallback: manual calculation
    const baseTuitionFee = classDetails?.tuitionFee ?? currentTuition;
    const enrollmentDate = new Date();
    const firstPeriodProrated =
      classDetails?.prorateTuition && studioDetails?.prorationEnabled !== false
        ? calculateProratedTuition({
            tuitionFee: baseTuitionFee,
            tuitionBillingCycle: classDetails.tuitionBillingCycle,
            startDate: classDetails.startDate,
            endDate: classDetails.endDate,
            billingDay: classDetails.billingDay,
            billingFrequencyDays: classDetails.billingFrequencyDays,
            enrollmentDate,
            prorationBasis: studioDetails?.prorationBasis,
            classDays: classDetails.days,
            timeZone: studioDetails?.timezone,
          })
        : baseTuitionFee;

    const remainingPeriods = Math.max(
      0,
      (totalPaymentsData?.totalPayments ?? 1) - 1,
    );
    const remainingPeriodsTuition = remainingPeriods * baseTuitionFee;

    return firstPeriodProrated + remainingPeriodsTuition;
  }, [
    collectUpfront,
    classDetails?.tuitionBillingCycle,
    classDetails?.prorateTuition,
    studioDetails?.prorationEnabled,
    studioDetails?.prorationBasis,
    studioDetails?.timezone,
    classDetails?.startDate,
    classDetails?.endDate,
    classDetails?.billingDay,
    classDetails?.billingFrequencyDays,
    classDetails?.days,
    classDetails?.tuitionFee,
    currentTuition,
    totalPaymentsData?.totalPayments,
    matchedEnrollmentPayments,
    effectiveTuition,
    hasManualUpfrontTuitionOverride,
    tuitionValue,
  ]);

  // Calculate discount from API, scaling for upfront payments if needed
  const getCalculatedDiscountFromAPI = useMemo(() => {
    if (!calculateDiscountByEntityData?.totalDiscount) return 0;

    if (collectUpfront && classDetails?.tuitionBillingCycle !== 'One Time') {
      // Use backend first payment amount when available
      const backendFirstPayment = !hasManualUpfrontTuitionOverride
        ? matchedEnrollmentPayments?.payments?.[0]
        : undefined;
      const firstPeriodAmount = backendFirstPayment
        ? backendFirstPayment.amount
        : effectiveTuition;

      if (firstPeriodAmount === 0) {
        return calculateDiscountByEntityData.totalDiscount;
      }

      const scaleFactor = getUpfrontTuition / firstPeriodAmount;
      return calculateDiscountByEntityData.totalDiscount * scaleFactor;
    }

    return calculateDiscountByEntityData.totalDiscount;
  }, [
    calculateDiscountByEntityData?.totalDiscount,
    collectUpfront,
    classDetails?.tuitionBillingCycle,
    matchedEnrollmentPayments,
    effectiveTuition,
    getUpfrontTuition,
    hasManualUpfrontTuitionOverride,
  ]);

  // Calculate session fees (accounting for whether they're already paid)
  const perStudentFee = useMemo(() => {
    return !sessionFeeStatusData?.isPerStudentFeePaid
      ? (sessionFeeStatusData?.perStudentFee ?? 0)
      : 0;
  }, [
    sessionFeeStatusData?.isPerStudentFeePaid,
    sessionFeeStatusData?.perStudentFee,
  ]);

  const perFamilyFee = useMemo(() => {
    return !sessionFeeStatusData?.isPerFamilyFeePaid
      ? (sessionFeeStatusData?.perFamilyFee ?? 0)
      : 0;
  }, [
    sessionFeeStatusData?.isPerFamilyFeePaid,
    sessionFeeStatusData?.perFamilyFee,
  ]);

  // Z04 / ES14: discount % covers all summary charges; scholarship is tuition only.
  const calculateDiscount = () => {
    if (!classDetails) return 0;

    let tuitionForDiscount: number;
    if (collectUpfront) {
      tuitionForDiscount = getUpfrontTuition;
    } else if (
      isHourlyLastMonthCheckout ||
      (classDetails.prorateTuition && studioDetails?.prorationEnabled !== false)
    ) {
      tuitionForDiscount = effectiveTuition;
    } else {
      tuitionForDiscount = currentTuition;
    }

    const summaryTuition = collectUpfront
      ? getUpfrontTuition
      : effectiveTuition;
    const registrationFee =
      typeof classDetails.registrationFeeAmount === 'number'
        ? classDetails.registrationFeeAmount
        : 0;
    const costumeFee =
      typeof classDetails.costumeFee === 'number' ? classDetails.costumeFee : 0;

    return calculateManualEnrollmentDiscount({
      discountType,
      discountCategory,
      discountAmount,
      discountPercentage,
      tuitionAmount:
        discountCategory === 'scholarship'
          ? tuitionForDiscount
          : summaryTuition,
      otherCharges: registrationFee + costumeFee + perStudentFee + perFamilyFee,
    });
  };

  const concessionScopeNote = enrollmentConcessionScopeNote(discountCategory);

  const classDetailsWithOptionalSession = classDetails as
    | {
        session?: {
          billingDate?: string;
          chargeLastMonthAtCheckout?: boolean;
          chargeSessionFeesAtEnrollment?: boolean;
        };
      }
    | undefined;

  const isSessionBillingDateFuture = useMemo(() => {
    const todayStr = formatLocalDateKey(new Date());
    const billingDateStr = getLocalDateKey(
      classDetailsWithOptionalSession?.session?.billingDate,
    );
    return !!billingDateStr && todayStr < billingDateStr;
  }, [classDetailsWithOptionalSession?.session?.billingDate]);

  const isClassPaymentScheduled =
    !collectUpfront &&
    isSessionBillingDateFuture &&
    !classDetailsWithOptionalSession?.session?.chargeLastMonthAtCheckout;

  const areSessionFeesScheduled =
    isClassPaymentScheduled &&
    !classDetailsWithOptionalSession?.session?.chargeSessionFeesAtEnrollment;

  const calculateEnrollmentTotal = () => {
    if (!classDetails) return 0;

    const tuitionFee = collectUpfront ? getUpfrontTuition : effectiveTuition;
    const registrationFee =
      typeof classDetails.registrationFeeAmount === 'number'
        ? classDetails.registrationFeeAmount
        : 0;
    const costumeFee =
      typeof classDetails.costumeFee === 'number' ? classDetails.costumeFee : 0;
    const manualDiscount = calculateDiscount();
    const discount =
      manualDiscount > 0 ? manualDiscount : getCalculatedDiscountFromAPI;

    return Math.max(
      0,
      tuitionFee +
        registrationFee +
        costumeFee +
        perStudentFee +
        perFamilyFee -
        discount,
    );
  };

  // Calculate amount due now using memoized tuition value.
  const calculateFinalAmount = () => {
    if (!classDetails) return 0;

    const tuitionFee = collectUpfront ? getUpfrontTuition : effectiveTuition;
    const registrationFee =
      typeof classDetails.registrationFeeAmount === 'number'
        ? classDetails.registrationFeeAmount
        : 0;
    const costumeFee =
      typeof classDetails.costumeFee === 'number' ? classDetails.costumeFee : 0;
    const manualDiscount = calculateDiscount();
    const discount =
      manualDiscount > 0 ? manualDiscount : getCalculatedDiscountFromAPI;

    const classFeeTotal = isClassPaymentScheduled
      ? 0
      : registrationFee + costumeFee;
    const tuitionDueNow = isClassPaymentScheduled ? 0 : tuitionFee;
    const sessionFeeTotal = areSessionFeesScheduled
      ? 0
      : perStudentFee + perFamilyFee;
    const dueNowCharges = tuitionDueNow + classFeeTotal + sessionFeeTotal;
    const enrollmentTotal =
      tuitionFee + registrationFee + costumeFee + perStudentFee + perFamilyFee;
    const discountDueNow = calculateDueNowDiscount({
      discountTotal: discount,
      dueNowCharges,
      enrollmentTotal,
      discountCategory,
      discountType,
      discountPercentage,
      isClassPaymentScheduled,
    });

    return Math.max(
      0,
      tuitionDueNow + classFeeTotal + sessionFeeTotal - discountDueNow,
    );
  };

  const handleEnroll = () => {
    if (enrollmentType === 'trial') {
      void handleTrialEnroll();
      return;
    }
    if (isV2Enrollment) {
      void handlePreviewThenConfirm();
      return;
    }
    openEnrollmentConfirmation();
  };

  const handlePreviewThenConfirm = async () => {
    if (!selectedClass || !classDetails || !selectedSession) return;
    if (previewEnrollmentMutation.isPending) return;

    const studioId = classDetails.studio || studioDetails?._id;
    if (!studioId) {
      toast.error('Studio not found for enrollment preview');
      return;
    }

    // ENR-1797: Do not decide PM requirement on the client (discount scope and
    // future obligation need BE math). Resolve a saved PM when we can and pass
    // it through; preview/start or add-student-to-entity enforce and return
    // the API message. V2 BE total-obligation gate: follow-up on enrollio-be.
    let paymentMethodId: string | undefined;
    if (!skipPaymentMethod) {
      if (isV2Enrollment) {
        paymentMethodId = resolvedPaymentMethodId;
      } else if (paymentMethod) {
        paymentMethodId = pickPaymentMethodId(
          savedPaymentMethods?.length
            ? savedPaymentMethods
            : parentData?.email && locationId
              ? await getParentSavedPaymentMethods({
                  email: parentData.email,
                  locationId,
                  parentId: effectiveParentId,
                })
              : undefined,
          paymentMethod,
        );
      }
    }

    try {
      const preview = await previewEnrollmentMutation.mutateAsync({
        studioId,
        source: 'studio_portal',
        mode: 'manual',
        discountCategory: discountCategory ?? undefined,
        discountType,
        discountAmount,
        discountPercentage,
        applyDiscountTo,
        requests: [
          {
            entityId: selectedClass.value,
            entityType: 'class',
            sessionId: selectedSession.value,
            collectUpfront,
            skipPaymentMethod,
            allowCapacityOverride: canOverrideClassCapacity,
            paymentMethodId: skipPaymentMethod ? undefined : paymentMethodId,
            students: [
              {
                studentId,
                ...(payingParentId ? { payingParentId } : {}),
              },
            ],
            ...(hasManualUpfrontTuitionOverride && tuitionValue !== undefined
              ? { customCreditAmount: tuitionValue }
              : {}),
          },
        ],
      });

      if (isAlreadyEnrolledNoop(preview)) {
        toast.warning(
          preview.message ||
            `Student already enrolled: ${(preview.existingEnrollmentsList ?? []).join(', ')}`,
        );
        return;
      }

      setResolvedPaymentMethodId(paymentMethodId);
      setEnrollmentPreview({
        amountDueTotal: preview.amountDueTotal ?? 0,
        charges: preview.charges ?? [],
      });
      openEnrollmentConfirmation();
    } catch {
      // Error toast handled by usePreviewEnrollment.onError
    }
  };

  // Reset isSubmitting when confirmation dialog closes
  useEffect(() => {
    if (!showConfirmation) {
      setIsSubmitting(false);
      setEnrollmentPreview(null);
      setResolvedPaymentMethodId(undefined);
      setStripeConfig(null);
    }
  }, [showConfirmation]);
  const handleTrialEnroll = async () => {
    if (createTrialEnrollmentMutation.isPending) {
      return;
    }
    if (!selectedSession) {
      toast.error('Please select a session for trial enrollment');
      return;
    }
    if (!selectedClass) {
      toast.error('Please select a class for trial enrollment');
      return;
    }
    if (!selectedSlot) {
      toast.error('Please select an available trial slot');
      return;
    }

    try {
      await createTrialEnrollmentMutation.mutateAsync({
        studentId,
        classId: selectedClass.value,
        trialStartTimeISO: selectedSlot.startTime,
        trialEndTimeISO: selectedSlot.endTime,
      });

      setDialogOpen(false);
      onTrialEnrollmentSuccess?.();
    } catch {
      // Error toast is already handled by mutation onError.
    }
  };

  const handleConfirmEnroll = async () => {
    if (!selectedClass || !classDetails || !selectedSession) return;
    if (isSubmitting) return; // Prevent double submit
    setIsSubmitting(true);

    const studioId = classDetails.studio || studioDetails?._id;
    if (!studioId) {
      toast.error('Studio not found for enrollment');
      setIsSubmitting(false);
      return;
    }

    try {
      if (isV2Enrollment) {
        const startResult = await startEnrollmentMutation.mutateAsync({
          studioId,
          source: 'studio_portal',
          mode: 'manual',
          discountCategory: discountCategory ?? undefined,
          discountType,
          discountAmount,
          discountPercentage,
          applyDiscountTo,
          requests: [
            {
              entityId: selectedClass.value,
              entityType: 'class',
              sessionId: selectedSession.value,
              collectUpfront,
              skipPaymentMethod,
              allowCapacityOverride: canOverrideClassCapacity,
              paymentMethodId: skipPaymentMethod
                ? undefined
                : resolvedPaymentMethodId,
              students: [
                {
                  studentId,
                  ...(payingParentId ? { payingParentId } : {}),
                },
              ],
              ...(hasManualUpfrontTuitionOverride && tuitionValue !== undefined
                ? { customCreditAmount: tuitionValue }
                : {}),
            },
          ],
        });

        const outcome = getStartEnrollmentOutcome(startResult);
        if (outcome.type !== 'success') {
          return;
        }

        setShowConfirmation(false);
        setDialogOpen(false);
        return;
      }

      const legacyPaymentMethod =
        paymentMethod === PaymentMethod.CARD ||
        paymentMethod === PaymentMethod.US_BANK_ACCOUNT ||
        paymentMethod === PaymentMethod.AU_BECS_DEBIT
          ? paymentMethod
          : null;

      const payload = {
        studioId,
        entityId: selectedClass.value,
        studentIds: [studentId],
        entityType: 'class' as const,
        paymentMethod: legacyPaymentMethod,
        discountCategory,
        discountType,
        discountAmount,
        discountPercentage,
        applyDiscountTo,
        collectUpfront,
        finalAmount: calculateFinalAmount(),
        sessionId: selectedSession.value,
        skipPaymentMethod,
        allowCapacityOverride: canOverrideClassCapacity,
      };

      if (hasManualUpfrontTuitionOverride && tuitionValue !== undefined) {
        Object.assign(payload, { customCreditAmount: tuitionValue });
      }

      const response = await addStudentToEntityMutation.mutateAsync(payload);

      if (
        response.message === 'Payment method not found' &&
        response.clientSecret &&
        response.stripePublicApiKey
      ) {
        setStripeConfig({
          clientSecret: response.clientSecret,
          publishableKey: response.stripePublicApiKey,
          stripeAccount: response.stripeAccount,
        });
        return;
      }

      // ENR-3580: keep dialog open unless at least one student actually enrolled.
      // Mutation toasts already cover already_enrolled / error / success.
      const enrolledCount =
        response.summary?.enrolled ??
        (response.results ?? []).filter(
          (result) => result.enrollmentStatus === 'enrolled',
        ).length;
      if (enrolledCount === 0) {
        return;
      }

      setShowConfirmation(false);
      setDialogOpen(false);
    } catch (error) {
      console.error('Enrollment failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveTuition = () => {
    if (isTuitionValid()) {
      setIsEditingTuition(false);
      setTuitionError(null);
    }
  };

  const handleCancelTuition = () => {
    // Reset to appropriate value based on collectUpfront
    if (
      collectUpfront &&
      totalPaymentsData?.totalPayments &&
      classDetails?.tuitionBillingCycle !== 'One Time'
    ) {
      if (classDetails) {
        setTuitionValue(
          classDetails.tuitionFee * totalPaymentsData.totalPayments,
        );
      }
    } else {
      setTuitionValue(classDetails?.tuitionFee);
    }
    setIsEditingTuition(false);
  };

  return (
    <>
      <Dialog
        modal={false}
        open={dialogOpen}
        onOpenChange={handleEnrollDialogOpenChange}
      >
        <DialogContent
          // overflow-hidden + inner scroll: keeps Enrollment Summary in flow
          // (ENR-3633). modal={false} still lets Session/Class menus receive
          // mouse clicks without the dialog treating them as outside dismiss.
          className='flex max-h-[min(90vh,calc(100dvh-2rem))] flex-col overflow-hidden sm:max-w-[500px]'
          onOpenAutoFocus={(e) => {
            // Avoid focusing Enrollment Type on open (looks like an active/focused border).
            e.preventDefault();
          }}
          onFocusOutside={blockEnrollDialogFocusDismiss}
          onInteractOutside={blockEnrollDialogDismiss}
          onPointerDownOutside={blockEnrollDialogDismiss}
        >
          <DialogHeader className='shrink-0'>
            <DialogTitle>Enroll Student in Class</DialogTitle>
            <DialogDescription>
              Select a session and class to enroll the student.
            </DialogDescription>
          </DialogHeader>

          <div className='grid min-h-0 flex-1 gap-4 overflow-y-auto overscroll-contain py-4'>
            {/* Session Selection */}
            <div className='grid gap-2'>
              <Label htmlFor='enrollmentType'>Enrollment Type</Label>
              <Select
                value={enrollmentType}
                onValueChange={(value) => {
                  if (value === 'regular' || value === 'trial') {
                    setEnrollmentType(value);
                  }
                  // Drop focus after choose so the trigger doesn't keep the active border.
                  requestAnimationFrame(() => {
                    if (document.activeElement instanceof HTMLElement) {
                      document.activeElement.blur();
                    }
                  });
                }}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue placeholder='Select enrollment type' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='regular'>Regular Enrollment</SelectItem>
                  <SelectItem value='trial'>Trial Class</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Session Selection */}
            <div className='grid gap-2 overflow-visible'>
              <Label htmlFor='session'>
                Select Session {preSelectedSession && '(Auto-selected)'}
              </Label>
              <SessionSelector
                session={selectedSession}
                onChange={(value) => {
                  if (!preSelectedSession) {
                    setSelectedSession(value ?? null);
                  }
                }}
                excludePastSessions
              />
            </div>

            {/* Class Selection */}
            <div className='grid gap-2 overflow-visible'>
              <Label htmlFor='class'>
                Select Class {preSelectedClass && '(Auto-selected)'}
              </Label>
              <ClassSelector
                selectedSessionId={selectedSession?.value}
                selectedClass={selectedClass}
                onChange={(value) => {
                  if (!preSelectedClass) {
                    setSelectedClass(value ?? null);
                  }
                }}
              />
            </div>

            {enrollmentType === 'trial' && (
              <>
                <div className='grid gap-2'>
                  <Label htmlFor='trialDate'>Select date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id='trialDate'
                        type='button'
                        variant='outline'
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !trialDate && 'text-muted-foreground',
                        )}
                      >
                        {trialDate ? trialDate : 'Pick a class day'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-auto p-0' align='start'>
                      <Calendar
                        mode='single'
                        selected={
                          trialDate
                            ? new Date(`${trialDate}T12:00:00`)
                            : undefined
                        }
                        onSelect={(date) => {
                          if (!date) return;
                          const localDate = new Date(
                            Date.UTC(
                              date.getFullYear(),
                              date.getMonth(),
                              date.getDate(),
                              12,
                            ),
                          )
                            .toISOString()
                            .slice(0, 10);
                          setTrialDate(localDate);
                          setSelectedSlot(null);
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const current = new Date(date);
                          current.setHours(0, 0, 0, 0);
                          if (current < today) return true;
                          if (allowedTrialDayIndexes.size === 0) return false;
                          return !allowedTrialDayIndexes.has(date.getDay());
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                  {allowedTrialDays.length > 0 && (
                    <p className='text-xs text-muted-foreground'>
                      Trial classes can only be booked on:{' '}
                      {allowedTrialDays.join(', ')}
                    </p>
                  )}
                </div>
                {trialDate && selectedClass?.value && (
                  <div className='grid gap-2'>
                    {trialScheduleWarnings.length > 0 && (
                      <div className='rounded-lg border border-amber-300 bg-amber-50 p-3'>
                        <p className='text-sm font-semibold text-amber-900'>
                          Schedule mismatch
                        </p>
                        {trialScheduleWarnings.map((warning) => (
                          <p
                            key={`${warning.date}-${warning.message}`}
                            className='mt-1 text-xs text-amber-800'
                          >
                            {warning.message}
                          </p>
                        ))}
                      </div>
                    )}
                    <Label>
                      Available CRM slots
                      {trialSlotsData?.timezone && (
                        <span className='ml-1 font-normal text-muted-foreground'>
                          ({trialSlotsData.timezone})
                        </span>
                      )}
                    </Label>
                    {isFetchingSlots ? (
                      <p className='text-sm text-muted-foreground'>
                        Loading available slots...
                      </p>
                    ) : isSlotsError ? (
                      <p className='text-sm text-destructive'>
                        Failed to load slots. Check that the class has a CRM
                        calendar linked and try again.
                      </p>
                    ) : trialSlotsData?.slots.length ? (
                      <div className='flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-md border p-2'>
                        {trialSlotsData.slots.map((slot) => {
                          const slotTimezone =
                            trialSlotsData.timezone ??
                            studioTimezone ??
                            undefined;
                          const label =
                            formatTrialTimeRange(
                              slot.startTime,
                              slot.endTime,
                              slotTimezone,
                            ) ?? '-';
                          const isSelected =
                            selectedSlot?.startTime === slot.startTime &&
                            selectedSlot.endTime === slot.endTime;
                          return (
                            <Button
                              key={`${slot.date}-${slot.startTime}-${slot.endTime}`}
                              type='button'
                              variant={isSelected ? 'default' : 'outline'}
                              size='sm'
                              onClick={() => {
                                setSelectedSlot(slot);
                              }}
                            >
                              {label}
                            </Button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className='text-sm text-muted-foreground'>
                        No available slots for this date. Try another date or
                        check calendar availability in CRM.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Discount Type Dropdown */}
            {enrollmentType === 'regular' && (
              <div className='grid gap-2'>
                <Label htmlFor='discountType'>Discount</Label>
                <Select
                  value={discountCategory ?? undefined}
                  onValueChange={(value) => {
                    if (value === 'discount' || value === 'scholarship') {
                      setDiscountCategory(value);
                    } else {
                      setDiscountCategory(null);
                      setDiscountAmount(0);
                      setDiscountPercentage(0);
                    }
                  }}
                >
                  <SelectTrigger className='w-full'>
                    <SelectValue placeholder='Select discount type' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='none'>None</SelectItem>
                    <SelectItem value='discount'>Discount</SelectItem>
                    <SelectItem value='scholarship'>Scholarship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Discount Value Input - Only shown if discount type is not "none" */}
            {enrollmentType === 'regular' && discountCategory !== null && (
              <>
                <div className='grid gap-2'>
                  <Label>Enter {discountCategory}</Label>
                  <RadioGroup
                    value={discountType}
                    onValueChange={(value) => {
                      setDiscountType(value as 'amount' | 'percentage');
                    }}
                    className='flex space-x-4'
                  >
                    <div className='flex items-center space-x-2'>
                      <RadioGroupItem value='amount' id='amount' />
                      <Label htmlFor='amount'>Amount</Label>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <RadioGroupItem value='percentage' id='percentage' />
                      <Label htmlFor='percentage'>Percentage</Label>
                    </div>
                  </RadioGroup>
                </div>

                {discountType === 'amount' ? (
                  <div className='grid gap-2'>
                    <Label htmlFor='discountAmount'>
                      Enter amount ({currencyData?.symbol})
                    </Label>
                    <Input
                      id='discountAmount'
                      type='number'
                      min='0'
                      step='0.01'
                      value={discountAmount}
                      onChange={(e) => {
                        setDiscountAmount(
                          Number.parseFloat(e.target.value) || 0,
                        );
                      }}
                      placeholder='0.00'
                    />
                  </div>
                ) : (
                  <div className='grid gap-2'>
                    <Label htmlFor='discountPercentage'>
                      Enter percentage (%)
                    </Label>
                    <Input
                      id='discountPercentage'
                      type='number'
                      min='0'
                      max='100'
                      step='1'
                      value={discountPercentage}
                      onChange={(e) => {
                        setDiscountPercentage(
                          Number.parseInt(e.target.value) || 0,
                        );
                      }}
                      placeholder='0'
                    />
                  </div>
                )}
              </>
            )}

            {/* Apply Discount To - Only shown if discount type is not "none" and billing type is recurring */}
            {enrollmentType === 'regular' && discountCategory !== null && (
              <div className='grid gap-2'>
                <Label>Apply {discountCategory} To</Label>
                <RadioGroup
                  value={applyDiscountTo}
                  onValueChange={(value) => {
                    setApplyDiscountTo(value as 'first' | 'all');
                  }}
                  className='space-y-2'
                >
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem value='first' id='first-payment' />
                    <Label htmlFor='first-payment'>First Payment Only</Label>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem value='all' id='all-payments' />
                    <Label htmlFor='all-payments'>
                      All Future Payments for This Class
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {isV2Enrollment && enrollmentType === 'regular' && (
              <PayingParentSelect
                studentId={studentId}
                value={payingParentId}
                onChange={(next) => {
                  setPayingParentId(next);
                  setEnrollmentPreview(null);
                  setResolvedPaymentMethodId(undefined);
                }}
              />
            )}

            {/* Upfront Payment Option */}
            {enrollmentType === 'regular' && (
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='upfront'
                  checked={collectUpfront}
                  onCheckedChange={(checked) => {
                    setSkipPaymentMethod(false);
                    setCollectUpfront(checked === true);
                    if (isV2Enrollment) {
                      if (!resolvedPaymentMethodId) {
                        const method = pickEnrollmentCatalogDefaultMethod(
                          enrollmentPaymentMethods,
                        );
                        if (method) {
                          setResolvedPaymentMethodId(method.id);
                          setPaymentMethod(toOnlinePaymentMethod(method.type));
                        }
                      }
                    } else if (
                      paymentMethod === undefined &&
                      firstAllowedMethod
                    ) {
                      setPaymentMethod(firstAllowedMethod);
                    }
                  }}
                  disabled={skipPaymentMethod}
                />
                <Label htmlFor='upfront' className='text-sm'>
                  Collect full payment upfront (the parent will be charged
                  immediately)
                </Label>
              </div>
            )}

            {/* Payment Method Selection */}
            {enrollmentType === 'regular' && (
              <div className='grid gap-2'>
                <Label>Payment Method</Label>
                {isV2Enrollment ? (
                  isLoadingEnrollmentPms ? (
                    <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                      <Loader2 className='size-3.5 animate-spin' />
                      Loading saved payment methods…
                    </p>
                  ) : (enrollmentPaymentMethods?.savedMethods.length ?? 0) >
                    0 ? (
                    <div className='space-y-2'>
                      <p className='text-sm font-medium text-muted-foreground'>
                        Saved payment methods
                      </p>
                      <Select
                        value={resolvedPaymentMethodId ?? ''}
                        disabled={skipPaymentMethod}
                        onValueChange={(id) => {
                          const selected =
                            enrollmentPaymentMethods?.savedMethods.find(
                              (method) => method.id === id,
                            );
                          if (!selected) return;
                          setSkipPaymentMethod(false);
                          setResolvedPaymentMethodId(selected.id);
                          setPaymentMethod(
                            toOnlinePaymentMethod(selected.type),
                          );
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select a saved payment method' />
                        </SelectTrigger>
                        <SelectContent>
                          {enrollmentPaymentMethods?.savedMethods.map(
                            (method) => (
                              <SelectItem key={method.id} value={method.id}>
                                {formatEnrollmentSavedMethodLabel(method)}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <p className='text-sm text-muted-foreground'>
                      No saved payment methods for this parent. Save a card
                      first, or check “No payment method needed”.
                    </p>
                  )
                ) : (
                  <RadioGroup
                    value={paymentMethod}
                    onValueChange={(value) => {
                      setPaymentMethod(value as OnlinePaymentMethod);
                    }}
                    className='flex space-x-4'
                    disabled={skipPaymentMethod}
                  >
                    {isCardAllowed && (
                      <PaymentMethodOption
                        id={PaymentMethod.CARD}
                        value={PaymentMethod.CARD}
                        label={PAYMENT_METHOD_LABELS.card.short}
                        checkData={checkCardPaymentMethodData}
                      />
                    )}
                    {isAchAllowed && (
                      <PaymentMethodOption
                        id={PaymentMethod.US_BANK_ACCOUNT}
                        value={PaymentMethod.US_BANK_ACCOUNT}
                        label={PAYMENT_METHOD_LABELS.us_bank_account.short}
                        checkData={checkBankPaymentMethodData}
                      />
                    )}
                    {isBecsAllowed && (
                      <PaymentMethodOption
                        id={PaymentMethod.AU_BECS_DEBIT}
                        value={PaymentMethod.AU_BECS_DEBIT}
                        label={PAYMENT_METHOD_LABELS.au_becs_debit.short}
                        checkData={checkBecsPaymentMethodData}
                      />
                    )}
                  </RadioGroup>
                )}
              </div>
            )}

            {enrollmentType === 'regular' && (
              <div className='flex items-start space-x-2'>
                <Checkbox
                  className='mt-0.5'
                  id='skip-payment-method'
                  checked={skipPaymentMethod}
                  onCheckedChange={(checked) => {
                    if (checked === true) {
                      setCollectUpfront(false);
                      setPaymentMethod(undefined);
                      setResolvedPaymentMethodId(undefined);
                    } else if (isV2Enrollment) {
                      const defaultMethod = pickEnrollmentCatalogDefaultMethod(
                        enrollmentPaymentMethods,
                      );
                      if (defaultMethod) {
                        setResolvedPaymentMethodId(defaultMethod.id);
                        setPaymentMethod(
                          toOnlinePaymentMethod(defaultMethod.type),
                        );
                      }
                    } else if (firstAllowedMethod) {
                      setPaymentMethod(firstAllowedMethod);
                    }
                    setSkipPaymentMethod(checked === true);
                  }}
                  disabled={collectUpfront}
                />
                <Label htmlFor='skip-payment-method' className='text-sm'>
                  No payment method needed; the parent will pay the outstanding
                  balance later.
                </Label>
              </div>
            )}

          {/* Enrollment Summary — inside the scroll body so it cannot overlay fields */}
          {selectedClass && enrollmentType === 'regular' && (
            <Card className='relative z-0 bg-muted/50'>
              <CardContent className='pt-6'>
                <h3 className='mb-2 font-medium'>Enrollment Summary</h3>
                {isClassFull && (
                  <div className='mb-3 rounded-lg border-2 border-red-500 bg-red-50 p-3'>
                    <p className='text-sm font-semibold text-red-800'>
                      ⚠️ Class is at Capacity
                    </p>
                    <p className='mt-1 text-xs text-red-700'>
                      This class has reached its maximum capacity (
                      {classDetails?.maxSize} students). Current enrollment:{' '}
                      {(classDetails?.maxSize ?? 0) -
                        (classDetails?.remainingSeats ?? 0)}
                      /{classDetails?.maxSize}.{' '}
                      {canOverrideClassCapacity
                        ? 'Your role allows you to override capacity and continue.'
                        : 'Enrollment is not available at this time.'}
                    </p>
                  </div>
                )}
                <div className='space-y-1 text-sm'>
                  {selectedSession && (
                    <div className='flex justify-between'>
                      <span>Session:</span>
                      <span>{selectedSession.label}</span>
                    </div>
                  )}
                  <div className='flex justify-between'>
                    <span>Class:</span>
                    <span>{classDetails?.title}</span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Session Fee per Student:</span>
                    <span>
                      {currencyData?.symbol}
                      {perStudentFee}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Session Fee per Family:</span>
                    <span>
                      {currencyData?.symbol}
                      {perFamilyFee}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Costume Fee:</span>
                    <span>
                      {currencyData?.symbol}
                      {classDetails?.costumeFee}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span>Membership Fee:</span>
                    <span>
                      {currencyData?.symbol}
                      {classDetails?.registrationFeeAmount ?? 0}
                    </span>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span>
                      Tuition
                      {!collectUpfront &&
                        currentTuition !== effectiveTuition &&
                        ' (Prorated)*'}
                      :
                    </span>
                    {isEditingTuition ? (
                      <div className='flex flex-col items-end'>
                        <div className='flex items-center space-x-2'>
                          <Input
                            type='number'
                            className='h-7 w-20 p-2'
                            value={tuitionValue ?? ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              // Store the raw value without any post-processing
                              // (the display logic will handle it)
                              setTuitionValue(
                                inputValue === ''
                                  ? undefined
                                  : Math.round(Number(inputValue)),
                              );

                              if (inputValue === '') {
                                setTuitionError('Please enter a value');
                              } else if (Number(inputValue) <= 0) {
                                setTuitionError('Value must be greater than 0');
                              } else {
                                setTuitionError(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              // Only prevent 'e', '+', '-' characters
                              if (['e', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onBlur={(e) => {
                              // Validate on blur to catch values changed by arrow keys
                              const value = Number(e.target.value);
                              if (e.target.value === '') {
                                setTuitionError('Please enter a value');
                              } else if (value <= 0) {
                                setTuitionError('Value must be greater than 0');
                              } else {
                                setTuitionValue(Math.round(value));
                                setTuitionError(null);
                              }
                            }}
                            step='1'
                            min='1'
                          />
                          <Button
                            onClick={() => {
                              handleSaveTuition();
                            }}
                            variant='ghost'
                            size='icon'
                            className='size-7 p-0 text-green-500 hover:text-green-700'
                            disabled={!isTuitionValid()}
                          >
                            <Check className='size-4' />
                          </Button>
                          <Button
                            onClick={() => {
                              handleCancelTuition();
                            }}
                            variant='ghost'
                            size='icon'
                            className='size-7 p-0 text-red-500 hover:text-red-700'
                          >
                            <X className='size-4' />
                          </Button>
                        </div>
                        {tuitionError && (
                          <span className='mt-1 text-xs text-red-500'>
                            {tuitionError}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className='flex items-center space-x-2'>
                        <span>
                          {currencyData?.symbol}
                          {collectUpfront
                            ? getUpfrontTuition.toFixed(2)
                            : effectiveTuition.toFixed(2)}
                          {!collectUpfront &&
                            currentTuition !== effectiveTuition && (
                              <span className='ml-1 text-xs text-muted-foreground line-through'>
                                ({currencyData?.symbol}
                                {currentTuition})
                              </span>
                            )}
                        </span>
                        {collectUpfront && (
                          <Button
                            onClick={() => {
                              setIsEditingTuition(true);
                            }}
                            variant='ghost'
                            size='icon'
                            className='size-7 h-fit p-0'
                          >
                            <Pencil className='size-4' />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {classDetails?.prorateTuition && !collectUpfront && (
                    <div className='flex justify-between text-xs text-muted-foreground'>
                      <span className='italic'>
                        *Proration will be calculated based on enrollment date
                      </span>
                    </div>
                  )}
                  {calculateDiscount() > 0 ? (
                    <>
                      <div className='flex justify-between'>
                        <span>
                          {discountCategory === 'scholarship'
                            ? 'Scholarship:'
                            : 'Discount:'}
                        </span>
                        <span>
                          {discountType === 'percentage'
                            ? `${discountPercentage}% (${currencyData?.symbol}${calculateDiscount().toFixed(2)})`
                            : `${currencyData?.symbol}${calculateDiscount().toFixed(2)}`}
                        </span>
                      </div>
                      {concessionScopeNote && (
                        <div className='text-xs text-muted-foreground'>
                          {concessionScopeNote}
                        </div>
                      )}
                      {classDetails?.tuitionBillingCycle !== 'One Time' && (
                        <div className='flex justify-between'>
                          <span>Apply To:</span>
                          <span>
                            {applyDiscountTo === 'first'
                              ? 'First Payment Only'
                              : 'All Payments'}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className='flex justify-between'>
                      <span>Discount:</span>
                      <span>
                        {currencyData?.symbol}
                        {getCalculatedDiscountFromAPI.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className='flex justify-between'>
                    <span>Billing Type:</span>
                    <span>
                      {collectUpfront
                        ? 'Full Payment'
                        : formatBillingCycle(
                            classDetails?.tuitionBillingCycle,
                            classDetails?.billingFrequencyDays,
                          )}
                    </span>
                  </div>
                  {/* {collectUpfront &&
                    classDetails?.tuitionBillingCycle !== 'One Time' && (
                      <div className='flex justify-between'>
                        <span>Total Payments:</span>
                        <span>
                          {totalPaymentsData?.totalPayments ?? 1} payments
                        </span>
                      </div>
                    )} */}
                  <div className='mt-2 flex justify-between border-t pt-2 font-medium'>
                    <span>Final Amount:</span>
                    <span>
                      {currencyData?.symbol}
                      {calculateEnrollmentTotal().toFixed(2)}
                      {!collectUpfront &&
                        currentTuition !== effectiveTuition &&
                        ' (prorated)'}
                      {collectUpfront && classDetails?.prorateTuition && (
                        <span className='ml-1 text-xs font-normal text-muted-foreground'>
                          (estimate)
                        </span>
                      )}
                    </span>
                  </div>
                  {collectUpfront && classDetails?.prorateTuition && (
                    <div className='mt-1 text-xs italic text-muted-foreground'>
                      * Final amount calculated at enrollment time may vary
                      slightly
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          </div>

          <DialogFooter className='shrink-0 border-t bg-background pt-4'>
            <Button
              variant='outline'
              onClick={() => {
                setDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEnroll}
              disabled={
                !selectedClass ||
                (isV2Enrollment && previewEnrollmentMutation.isPending) ||
                (isV2Enrollment &&
                  enrollmentType === 'regular' &&
                  !skipPaymentMethod &&
                  (isLoadingEnrollmentPms || !resolvedPaymentMethodId)) ||
                (enrollmentType === 'trial' && trialButtonDisabled) ||
                (enrollmentType === 'regular' &&
                  isEditingTuition &&
                  !isTuitionValid()) ||
                (enrollmentType === 'regular' &&
                  isClassFull &&
                  !canOverrideClassCapacity)
              }
            >
              {enrollmentType === 'trial' &&
              createTrialEnrollmentMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Enrolling Trial Class...
                </>
              ) : isV2Enrollment && previewEnrollmentMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Calculating charges...
                </>
              ) : isClassFull &&
                enrollmentType === 'regular' &&
                !canOverrideClassCapacity ? (
                'Class at Capacity'
              ) : enrollmentType === 'trial' ? (
                'Enroll Trial Class'
              ) : (
                'Enroll'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <EnrollStudentConfirmation
        open={showConfirmation}
        onOpenChange={(open) => {
          if (open) {
            openEnrollmentConfirmation();
            return;
          }
          closeEnrollmentConfirmation();
        }}
        sessionData={selectedSession}
        classData={selectedClass}
        discountCategory={discountCategory}
        discountType={discountType}
        discountAmount={discountAmount}
        discountPercentage={discountPercentage}
        applyDiscountTo={applyDiscountTo}
        collectUpfront={collectUpfront}
        paymentDeferred={skipPaymentMethod}
        tuitionFee={collectUpfront ? getUpfrontTuition : effectiveTuition}
        finalAmount={
          enrollmentPreview?.amountDueTotal ?? calculateFinalAmount()
        }
        paymentMethod={
          paymentMethod === PaymentMethod.CARD ||
          paymentMethod === PaymentMethod.US_BANK_ACCOUNT ||
          paymentMethod === PaymentMethod.AU_BECS_DEBIT
            ? paymentMethod
            : undefined
        }
        cardSurchargeEligible={
          paymentMethod === PaymentMethod.CARD
            ? isV2Enrollment
              ? enrollmentPaymentMethods?.cardSurchargeEligible
              : checkCardPaymentMethodData?.cardSurchargeEligible
            : undefined
        }
        isLoading={
          (isV2Enrollment
            ? startEnrollmentMutation.isPending
            : addStudentToEntityMutation.isPending) || isSubmitting
        }
        onConfirm={handleConfirmEnroll}
        onCancel={closeEnrollmentConfirmation}
        stripeConfig={stripeConfig}
        perStudentFee={perStudentFee}
        perFamilyFee={perFamilyFee}
        sessionFeesScheduled={areSessionFeesScheduled}
        classFeesScheduled={isClassPaymentScheduled}
        isClassFull={isClassFull}
        canOverrideClassCapacity={canOverrideClassCapacity}
        parentId={effectiveParentId}
        calculatedDiscountFromAPI={getCalculatedDiscountFromAPI}
        previewCharge={enrollmentPreview?.charges[0]}
        paymentMethodId={resolvedPaymentMethodId}
      />
    </>
  );
};
